import { Fragment, type ReactNode } from "react";

// Minimal Markdown renderer scoped to the in-repo blog post format.
//
// Supported features (intentionally narrow — all content is authored in-repo,
// never user input, so we do not ship a full CommonMark parser):
//
//   # h1, ## h2, ### h3, paragraph, bold, italic, inline code,
//   fenced code block, blockquote, bullet list, numbered list
//
// Output is escaped — every text segment passes through React default
// escaping. No dangerouslySetInnerHTML, no HTML injection vector.

export function MarkdownPost({ source }: { source: string }) {
  const blocks = parseBlocks(source);
  return (
    <div className="prose-post">
      {blocks.map((block, i) => (
        <Fragment key={i}>{renderBlock(block, i)}</Fragment>
      ))}
    </div>
  );
}

type Block =
  | { kind: "h1"; text: string }
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "quote"; text: string }
  | { kind: "code"; text: string };

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (line.trim() === "") {
      i++;
      continue;
    }
    if (line.startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? "").startsWith("```")) {
        buf.push(lines[i] ?? "");
        i++;
      }
      i++;
      blocks.push({ kind: "code", text: buf.join("\n") });
      continue;
    }
    if (line.startsWith("### ")) {
      blocks.push({ kind: "h3", text: line.slice(4).trim() });
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      blocks.push({ kind: "h2", text: line.slice(3).trim() });
      i++;
      continue;
    }
    if (line.startsWith("# ")) {
      blocks.push({ kind: "h1", text: line.slice(2).trim() });
      i++;
      continue;
    }
    if (line.startsWith("> ")) {
      const buf: string[] = [line.slice(2)];
      i++;
      while (i < lines.length && (lines[i] ?? "").startsWith("> ")) {
        buf.push((lines[i] ?? "").slice(2));
        i++;
      }
      blocks.push({ kind: "quote", text: buf.join(" ") });
      continue;
    }
    if (/^[-*] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*] /.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^[-*] /, "").trim());
        i++;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i] ?? "")) {
        items.push((lines[i] ?? "").replace(/^\d+\.\s/, "").trim());
        i++;
      }
      blocks.push({ kind: "ol", items });
      continue;
    }
    const buf: string[] = [line];
    i++;
    while (i < lines.length) {
      const nxt = lines[i] ?? "";
      if (
        nxt.trim() === "" ||
        nxt.startsWith("#") ||
        nxt.startsWith("> ") ||
        nxt.startsWith("```") ||
        /^[-*] /.test(nxt) ||
        /^\d+\.\s/.test(nxt)
      ) {
        break;
      }
      buf.push(nxt);
      i++;
    }
    blocks.push({ kind: "p", text: buf.join(" ") });
  }
  return blocks;
}

function renderBlock(block: Block, i: number): ReactNode {
  switch (block.kind) {
    case "h1":
      return (
        <h1
          key={i}
          className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tightest text-ink-900 mt-2 mb-8"
        >
          {block.text}
        </h1>
      );
    case "h2":
      return (
        <h2
          key={i}
          className="text-2xl md:text-3xl font-bold text-ink-900 mt-12 mb-4"
        >
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3
          key={i}
          className="text-xl md:text-2xl font-bold text-ink-900 mt-8 mb-3"
        >
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p
          key={i}
          className="text-base md:text-lg text-ink-700 leading-relaxed my-5 max-w-prose"
        >
          {renderInline(block.text)}
        </p>
      );
    case "quote":
      return (
        <blockquote
          key={i}
          className="my-8 border-l-4 border-forest-700 bg-forest-50 px-6 py-4 rounded-r-xl text-ink-800 text-lg italic max-w-prose"
        >
          {renderInline(block.text)}
        </blockquote>
      );
    case "ul":
      return (
        <ul
          key={i}
          className="my-5 space-y-2 pl-6 text-base md:text-lg text-ink-700 list-disc marker:text-forest-700 max-w-prose"
        >
          {block.items.map((it, j) => (
            <li key={j} className="leading-relaxed">
              {renderInline(it)}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol
          key={i}
          className="my-5 space-y-2 pl-6 text-base md:text-lg text-ink-700 list-decimal marker:text-forest-700 marker:font-semibold max-w-prose"
        >
          {block.items.map((it, j) => (
            <li key={j} className="leading-relaxed">
              {renderInline(it)}
            </li>
          ))}
        </ol>
      );
    case "code":
      return (
        <pre
          key={i}
          className="my-6 overflow-x-auto rounded-xl bg-ink-900 text-ink-100 p-5 text-sm leading-relaxed max-w-prose"
        >
          <code>{block.text}</code>
        </pre>
      );
  }
}

// Inline rendering — handles bold, italic, inline code.
// Returns an array of React nodes so we do not use dangerouslySetInnerHTML.
// Order matters: code first (so backticks swallow asterisks), then bold, then italic.
function renderInline(text: string): ReactNode[] {
  const codeParts = splitOn(text, /`([^`]+)`/g, (m) => ({
    kind: "code" as const,
    value: m[1] ?? "",
  }));
  const out: ReactNode[] = [];
  let key = 0;
  for (const part of codeParts) {
    if (typeof part === "string") {
      const boldParts = splitOn(part, /\*\*([^*]+)\*\*/g, (m) => ({
        kind: "bold" as const,
        value: m[1] ?? "",
      }));
      for (const bp of boldParts) {
        if (typeof bp === "string") {
          const italicParts = splitOn(bp, /\*([^*]+)\*/g, (m) => ({
            kind: "italic" as const,
            value: m[1] ?? "",
          }));
          for (const ip of italicParts) {
            if (typeof ip === "string") {
              if (ip.length > 0) out.push(<Fragment key={key++}>{ip}</Fragment>);
            } else if (ip.kind === "italic") {
              out.push(<em key={key++}>{ip.value}</em>);
            }
          }
        } else if (bp.kind === "bold") {
          out.push(
            <strong key={key++} className="font-semibold text-ink-900">
              {bp.value}
            </strong>
          );
        }
      }
    } else if (part.kind === "code") {
      out.push(
        <code
          key={key++}
          className="rounded bg-ink-100 px-1.5 py-0.5 text-[0.9em] text-forest-900 font-mono"
        >
          {part.value}
        </code>
      );
    }
  }
  return out;
}

type Token<T> = string | T;

function splitOn<T>(
  src: string,
  re: RegExp,
  build: (m: RegExpExecArray) => T
): Token<T>[] {
  const out: Token<T>[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  re.lastIndex = 0;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) out.push(src.slice(last, m.index));
    out.push(build(m));
    last = m.index + m[0].length;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}
